import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import {
  CarrierAndService,
  carrierToTrademarkText,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { Text } from "@redotech/redo-web/text";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";

const CarrierServiceOption = memo(function CarrierServiceOption({
  option,
}: {
  option: CarrierAndService;
}) {
  const text = getCarrierServiceLabel(option.carrier, option.service);

  return (
    <Flex align="center" dir="row" gap="md" py="xs">
      <ShippingLogo carrier={option.carrier} size={LogoSize.MEDIUM} />
      <Text fontSize="xs" style={{ textWrap: "wrap" }}>
        {text}
      </Text>
    </Flex>
  );
});

export const CarrierAndServiceDropdown = memo(
  function CarrierAndServiceDropdown({
    options,
    selectedOption,
    onSelectionChange,
    placeholder = "Select carrier and service",
    itemsLoading = false,
  }: {
    options: readonly CarrierAndService[];
    selectedOption: CarrierAndService | null;
    onSelectionChange: (option: CarrierAndService) => void;
    placeholder?: string;
    itemsLoading?: boolean;
  }) {
    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchString, setSearchString] = useState("");

    const dropdownOptionItems: RedoListItem<CarrierAndService>[] = useMemo(
      () =>
        options.map((option) => ({
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "custom",
          render: <CarrierServiceOption option={option} />,
          useWrapper: true,
          searchText: getCarrierServiceLabel(option.carrier, option.service),
        })),
      [options],
    );

    const searcher = useMemo(
      () =>
        new Fuse(dropdownOptionItems, { keys: ["searchText"], threshold: 0.3 }),
      [dropdownOptionItems],
    );

    const filteredItems = useMemo(() => {
      if (!searchString.trim()) {
        return dropdownOptionItems;
      }
      return searcher.search(searchString).map((result) => result.item);
    }, [dropdownOptionItems, searchString, searcher]);

    const selectedItem = selectedOption
      ? {
          id: `${selectedOption.carrier}-${selectedOption.service}`,
          value: selectedOption,
          type: "custom" as const,
          useWrapper: true,
          render: <CarrierServiceOption option={selectedOption} />,
        }
      : undefined;

    const handleSelection = (item: RedoListItem<CarrierAndService>) => {
      onSelectionChange(item.value);
      setDropdownOpen(false);
      setSearchString("");
    };

    const handleSetDropdownOpen = (open: boolean) => {
      setDropdownOpen(open);
      if (!open) {
        setSearchString("");
      }
    };

    const carrierTrademarkText = selectedOption
      ? carrierToTrademarkText[selectedOption.carrier]
      : null;

    return (
      <>
        <RedoSingleSelectDropdownInputButton
          isDropdownOpen={dropdownOpen}
          onClick={() => handleSetDropdownOpen(!dropdownOpen)}
          placeholder={placeholder}
          ref={setDropdownButtonRef}
          selectedItem={selectedItem}
          size="xs"
        />
        <RedoListDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor
          items={filteredItems}
          itemSelected={(args) => handleSelection(args.item)}
          itemsLoading={itemsLoading}
          searchPlaceholder="Search carriers and services..."
          searchString={searchString}
          selectedItems={selectedItem?.id}
          setDropdownOpen={handleSetDropdownOpen}
          setSearchString={setSearchString}
          size="xs"
        />
        {carrierTrademarkText && (
          <Text
            fontSize="xxs"
            style={{ textWrap: "wrap" }}
            textColor="secondary"
          >
            {carrierTrademarkText}
          </Text>
        )}
      </>
    );
  },
);
